/* eslint-disable camelcase */
import linkifyHtml from 'linkify-html';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Alert, Card, Col, Image, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import GoogleWebStoryViewer from "../../../../../../frontend/components/components/utils/google_web_stories/GoogleWebStoryViewer";
import { INFLUENCERS_ENDPOINTS } from '../../../../../config/api';
import Button from '../../../../../shared/components/Button';
import { downloadMedia } from '../../../../../shared/components/DownloadButton/DownloadButton';
import { displayObjAttrValues } from '../../../../../shared/components/ObjectAttribute/ObjectAttribute';
import axios from '../../../../../utils/axiosConfig';
import { CONTENT_STATUSES, OFFER_STAGE } from '../../../../../utils/constants';
import useGWSScript from '../../../hooks/useGWSScript';
import BadgeStatus from '../BadgeStatus';
import CarouselViewer from '../CarouselViewer';
import './UserContentCard.scss';

/**
 * @typedef {Object} UserContentCardParams
 * @prop {import('../../../../../utils/types/types').UserPostDraft} userContent
 * @prop {'userPostDraft' | 'userPost'} userContentType
 * @prop {() => void} onToggle
 * @prop {string} offerStatus
 * @prop {boolean} showDraftSubmit
 *
 * @param {UserContentCardParams} params
 * @returns
 */
function UserContentCard({
  userContent,
  onToggle,
  offerStatus,
  showDraftSubmit,
  userContentType = 'userPostDraft',
}) {

  const { t } = useTranslation();
  /** @type {import('../../../../../reducers/offer.reducer').Offer} */
  const offer = useSelector(store => get(store, 'offer.offer'));
  const hasObjectAttributesMetadata = !!get(userContent, 'object_attribute_metadata.length', false);
  const isOtherDeliverable = userContent.platform === 'other' && userContentType === "userPost";

  useGWSScript();

  const media = () => {
    if (userContent.carousel_media_urls.length > 0) {
      return (
        <CarouselViewer userPostDraft={userContent} />
      );
    }

    if (!userContent.media_url && !userContent.url) {
      return ('');
    }
    if (userContent.is_google_web_story) {
      userContent.hide_qa_result = true;
      return <GoogleWebStoryViewer userPost={userContent} showDebugLink={false} />;
    }
    if (userContent.is_youtube) {
      return (
        <div className='media-container'>
          <div className="media youtube">
            <iframe allowFullScreen="allowFullScreen" width="100%" allowtransparency="true" frameBorder="0"
              src={`https://www.youtube.com/embed/${userContent.youtube_id}?ecver=1&amp;iv_load_policy=1&amp;rel=0&amp;yt:stretch=16:9&amp;autohide=1&amp;color=red&amp;width=560&amp;width=560`} />
          </div>
        </div>
      );
    } else if (userContent.is_video) {
      return (
        <div className='media-container'>
          <div className="media video">
            <video controls key={userContent.media_url}>
              <source src={userContent.media_url} />
            </video>
          </div>
        </div>
      );
    }
    return (
      <div className='media-container'>
        <div className="media img">
          <Image className="photo w-100" fluid src={userContent.media_url} />
        </div>
      </div>
    );
  };

  const markAsResolved = () => {
    setResolved(true);
    axios.put(`/api/v1/influencers/user_post_drafts/${userContent.id}/mark_as_resolved`).then(({ data }) => {
      if (data.success === true) {
        setResolved(true);
        setReviewStatus(CONTENT_STATUSES.NEEDS_REVIEW);
      }
    }).catch(err => {
      console.log(err);
      setResolved(false);
    });
  };


  const renderObjectAttributes = () => {
    let objectAttributes = {};

    if (userContentType === 'userPostDraft') {
      objectAttributes = get(userContent, 'object_attribute_values');
    } else {
      objectAttributes = get(userContent, 'user_post_object_attributes');
    }

    const objectAttributesKeys = Object.keys(objectAttributes || {});
    if (!objectAttributesKeys) return null;
    return objectAttributesKeys.map(oak => {
      const oav = objectAttributes[oak];

      if (!oav || !get(oav, 'values.length')) return null;
      return (<React.Fragment key={oav.code}>
        <div className="font-weight-bold mt-2" >
          {oav.column_label}
        </div>
        <div className="text-3">
          {displayObjAttrValues(oav.display_type, oav.values)}
        </div>
      </React.Fragment>);
    });
  };

  const [resolved, setResolved] = useState(userContent.marked_as_resolved);
  const [reviewStatus, setReviewStatus] = useState(userContent.am_review_status);

  const showReviewStatus = (reviewStatus) => {
    const non_submission_statuses = [CONTENT_STATUSES.REJECTED, CONTENT_STATUSES.ACCEPTED, CONTENT_STATUSES.ACCEPTED_BUT];
    if (showDraftSubmit && (!non_submission_statuses.includes(reviewStatus))) {
      return CONTENT_STATUSES.NEEDS_SUBMISSION;
    }

    return reviewStatus;
  };

  useEffect(() => {
    setReviewStatus(userContent.am_review_status);
  }, [userContent]);

  return (<Card className="mb-2">
    <Card.Header>
      <BadgeStatus className="m-0" status={showReviewStatus(reviewStatus)} />
      <h5 className="card-header-title">
        {offer.user_post_draft_types_enabled ?
          get(userContent, 'user_post_draft_type.label') :
          <React.Fragment>
            {userContent.story && t("influencers.content_approval.title.story")}
            {userContent.is_google_web_story && t("influencers.content_approval.title.gws")}
            {userContent.short && t("influencers.content_approval.title.short")}
            {!userContent.story && !userContent.is_google_web_story && !userContent.short &&
              t("influencers.content_approval.title.post")}
          </React.Fragment>
        }
      </h5>
    </Card.Header>
    <Card.Body>
      {media()}
      <div className="mt-3">
        {
          !userContent.short
            ? <div className="text-3">
              {userContent.caption &&
                <div dangerouslySetInnerHTML={{ __html: linkifyHtml(userContent.caption, { target: '_blank' }) }}></div>
              }
              {renderObjectAttributes()}
            </div>
            : <React.Fragment>
              <div className="text-3">
                {offer.user_post_draft_types_enabled ? get(userContent, 'user_post_draft_type.fields.title.label') : t("influencers.common.title")}
              </div>
              <div className="text-1 mb-2">
                {userContent.title}
              </div>
              <div className="text-3">
                {offer.user_post_draft_types_enabled ? get(userContent, 'user_post_draft_type.fields.caption.label') : t("influencers.common.description")}
              </div>
              <div className="text-2 mb-2">
                {userContent.caption &&
                  <div dangerouslySetInnerHTML={{ __html: linkifyHtml(userContent.caption, { target: '_blank' }) }}></div>
                }
              </div>
              <div className="text-3">
                {offer.user_post_draft_types_enabled ? get(userContent, 'user_post_draft_type.fields.location_tags.label') : t("influencers.common.location")}
              </div>
              <div className="text-2 mb-2">
                {userContent.location_tags ?
                  userContent.location_tags.map(tag => tag.name).join(', ')
                  : null}
              </div>
              <div className="text-3">
                {offer.user_post_draft_types_enabled ? get(userContent, 'user_post_draft_type.fields.general_tags.label') : t("influencers.common.tags")}
              </div>
              <div className="text-2 mb-2">
                {userContent.general_tags}
              </div>
              {renderObjectAttributes()}
            </React.Fragment>
        }
      </div>
      {!userContent.object_attributes_required_valid &&
        <Alert variant="danger">
          <i className="icon fal fa-exclamation-circle"></i>
          {t('influencers.content_approval.missing_information')}
        </Alert>}
    </Card.Body>
    <Card.Footer>
      <div className='d-flex flex-column align-items-end'>
        {
          hasObjectAttributesMetadata && (
            userContentType === 'userPost' ||
            isOtherDeliverable
          ) ?
            <div className="mb-2">
              <Button variant='tertiary' onClick={() => onToggle('objectAttributes')}>
                {t("influencers.content_approval.answer_questions")}
                <i className="fa fa-arrow-right ml-1" />
              </Button>
            </div>
            : null
        }
        {isOtherDeliverable ?
          <div className="mb-2">
            <Button variant="tertiary" onClick={() => onToggle('edit_draft')}>
              {t("influencers.content_approval.links.edit_deliverable")}
              <i className="fal fa-arrow-right icon-right"></i>
            </Button></div>
          : null}
        {userContentType === 'userPostDraft' && !isOtherDeliverable ?
          <React.Fragment>
            {
              userContent.am_review_status === CONTENT_STATUSES.ACCEPTED_BUT &&
              <div className="view-feedback-button mb-2">
                <Button variant="primary" data-cy='view_feedback' onClick={() => onToggle('view_feedback')}>
                  {t("influencers.content_approval.links.view_feedback")}
                  <i className="fal fa-arrow-right icon-right"></i>
                </Button>
              </div>
            }
            {userContent.is_google_web_story &&
              (userContent.am_review_status === CONTENT_STATUSES.REJECTED || resolved) &&
              <div className="mb-2">
                <Button className="p-0" variant="tertiary" href="#" size="sm" disabled={resolved} onClick={markAsResolved}>
                  {t("influencers.content_approval.links.mark_as_resolved")}
                  <i className={`fal ${resolved ? 'fa-check' : 'fa-arrow-right'} icon-right`}></i>
                </Button>
              </div>
            }
            {
              [CONTENT_STATUSES.REJECTED, CONTENT_STATUSES.PENDING_INFLUENCER, CONTENT_STATUSES.WAITING_AM].includes(userContent.am_review_status) &&
              OFFER_STAGE.CONTENT_APPROVAL.includes(offerStatus) &&
              <div className="edit-draft-button mb-2">
                <Button variant="primary" data-cy='edit_draft' onClick={() => onToggle('edit_draft')}>
                  {t("influencers.content_approval.links.edit_draft")}
                  <i className="fal fa-arrow-right icon-right"></i>
                </Button>
              </div>
            }
            {
              [CONTENT_STATUSES.ACCEPTED].includes(userContent.am_review_status) &&
              <div className="mb-2">
                <Button className="p-0" variant="tertiary" onClick={() => onToggle('view_draft')} size="sm">
                  {t("influencers.content_approval.links.view_draft")}
                  <i className="fal fa-arrow-right icon-right"></i>
                </Button>
              </div>
            }
            {
              [CONTENT_STATUSES.ACCEPTED, CONTENT_STATUSES.ACCEPTED_BUT].includes(userContent.am_review_status) &&
              <div className="download-asset-button mb-2">
                <Button
                  variant="secondary"
                  data-cy='download_asset'
                  onClick={() => downloadMedia(INFLUENCERS_ENDPOINTS.USER_POST_DRAFTS_MEDIA.GET(userContent.id))}
                >
                  {t("influencers.content_approval.links.download_asset")}
                  <i className="fal fa-download icon-right ml-2"></i>
                </Button>
              </div>
            }
            {
              [CONTENT_STATUSES.PENDING_INFLUENCER].includes(userContent.am_review_status) &&
              <div className="mb-2">
                <Button className="p-0" variant="tertiary" onClick={onToggle('upload')} size="sm">
                  {t("influencers.content_approval.links.upload")}
                  <i className="fal fa-arrow-right icon-right"></i>
                </Button>
              </div>
            }
          </React.Fragment> : null
        }
      </div>
    </Card.Footer>
  </Card >);
}

UserContentCard.propTypes = {
  userContent: PropTypes.object.isRequired,
  userContentType: PropTypes.string,
  onToggle: PropTypes.func,
  offerStatus: PropTypes.string,
  showDraftSubmit: PropTypes.bool,
  hasObjectAttributes: PropTypes.bool,
};

UserContentCard.defaultProps = {
  userContentType: 'userPostDraft',
  onToggle: null,
  offerStatus: null,
  showDraftSubmit: null,
  hasObjectAttributes: true,
};

export default React.memo(UserContentCard);
